import clsx from 'clsx';
import { useState } from 'react';

import { ONE, ZERO } from '@/constants';
import { useCurrentUser, useSessionsDashboard } from '@/hooks';
import { Typography } from '@/ui';

import CoachCard, { type CoachSessionState } from '../../Coach/Card';
import MemberBiweeklyReschedule from '../Member/MemberBiweeklyReschedule';

import type { Session, User } from '@/types';
import type { FC } from 'react';

import { resolveBiweeklyCoachVisibility } from '@/features/appointments/biweeklyCoachVisibility';
import {
  resolveMemberCoachMatchState,
  resolveQueryAuthorityState,
} from '@/features/appointments/memberCoachBookingState';
import { useAppointmentClock } from '@/features/appointments/useNativeAppointmentRows';

interface Props {
  coaches: User[];
  isLoading?: boolean;
  presentation?: 'default' | 'matched';
  refetch: () => void;
  user: User;
}

const ClientCoaches: FC<Props> = ({
  isLoading = false,
  coaches = [],
  presentation = 'default',
  user,
  refetch,
}) => {
  const [rescheduleCoach, setRescheduleCoach] = useState<User | null>(null);
  const now = useAppointmentClock(!isLoading);
  const {
    data: sessionsData,
    isError: isSessionsDashboardError,
    isFetching: isSessionsDashboardFetching,
    isLoading: isSessionsDashboardLoading,
    isSuccess: isSessionsDashboardSuccess,
  } = useSessionsDashboard();
  const {
    currentData: currentUser,
    isError: isCurrentUserError,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess,
  } = useCurrentUser();

  if (isLoading || isSessionsDashboardLoading) {
    return null;
  }

  if (coaches.length === ZERO) return null;

  // One authority rule for every dashboard-derived decision this component
  // makes: the cadence line, the per-coach CTA, and coach visibility. `isError`
  // is load-bearing — RTK Query retains the last successful `data` across a
  // rejected refetch, and /sessions/dashboard is invalidated by every booking
  // mutation, so an errored read still looks populated. `data` and `currentData`
  // coincide for this void-arg query, so presence is tested on `data`, the
  // snapshot every consumer below actually reads.
  const dashboardQuery = {
    hasCurrentData: !!sessionsData,
    isError: isSessionsDashboardError,
    isFetching: isSessionsDashboardFetching,
    isSuccess: isSessionsDashboardSuccess,
  };
  const hasAuthoritativeSessionsData =
    !!sessionsData && resolveQueryAuthorityState(dashboardQuery) === 'ready';
  const { cadence = {} } = sessionsData ?? {};
  const hasEnded = (session: Session) => {
    const endedAt = Date.parse(session.endedAt);
    return Number.isFinite(endedAt) && endedAt < now;
  };
  const biweeklyCoachIds = new Set([
    ...(sessionsData?.noUpcoming ?? []),
    ...Object.entries(sessionsData?.upcoming ?? {})
      .filter(([, session]) => !session.isChemistryCall || hasEnded(session))
      .map(([coachId]) => coachId),
  ]);
  const upcomingChemistryCoachIds = new Set(
    Object.entries(sessionsData?.upcoming ?? {})
      .filter(([, session]) => session.isChemistryCall && !hasEnded(session))
      .map(([coachId]) => coachId),
  );
  // Once the member holds an active Moments biweekly, the page narrows to that
  // coaching relationship and every other matched coach drops out.
  const { isVisibleCoachId } = resolveBiweeklyCoachVisibility({
    activeBiweekly: sessionsData?.activeBiweekly,
    coachIds: coaches.map((coach) => coach.id),
    dashboardQuery,
  });
  const visibleCoaches = coaches.filter((coach) => isVisibleCoachId(coach.id));
  const sessionStateFor = (coach: User): CoachSessionState => {
    if (!hasAuthoritativeSessionsData) return 'has_previous';
    if (sessionsData.activeBiweekly?.[coach.id]?.bookingSource === 'moments')
      return 'active_biweekly';
    if (upcomingChemistryCoachIds.has(coach.id)) return 'upcoming_chemistry';
    return biweeklyCoachIds.has(coach.id) ? 'has_previous' : 'no_previous';
  };

  return (
    <>
      {presentation !== 'matched' && (
        <Typography
          className="mt-8 select-none px-2 text-cement-400 md:px-0"
          variant="subtitle1"
        >
          Your coach{visibleCoaches.length > ONE ? 'es' : ''}
        </Typography>
      )}
      {/* MEN-5021 overflow follow-up: the second column is keyed to the width
          this container actually has, not to the viewport. `md:grid-cols-2`
          promised two columns from a 768px viewport, but the Sessions content
          column can be ~640px wide there, which produced the reported ~316px
          cards whose action row could not fit. `auto-fit` + a 440px minimum
          track drops to a single column until each card can hold a labelled
          primary CTA plus both icon actions on one line (measured need: 427px).
          `min(440px,100%)` keeps the track from overflowing below 440px. */}
      <div
        className={clsx({
          'gap-2 pt-4': presentation !== 'matched',
          'gap-4 pt-6': presentation === 'matched',
          'grid grid-cols-[repeat(auto-fit,minmax(min(440px,100%),1fr))]':
            visibleCoaches.length > ONE,
        })}
      >
        {visibleCoaches.map((coach) => {
          const sessionState = sessionStateFor(coach);
          const memberCoachMatchState = resolveMemberCoachMatchState(
            {
              currentCoachIds:
                currentUser?.coaches.map((currentCoach) => currentCoach.id) ??
                [],
              hasCurrentData: !!currentUser,
              isError: isCurrentUserError,
              isFetching: isCurrentUserFetching,
              isSuccess: isCurrentUserSuccess,
            },
            coach.id,
          );
          const activeBiweekly = hasAuthoritativeSessionsData
            ? sessionsData.activeBiweekly?.[coach.id]
            : undefined;
          return (
            <CoachCard
              key={coach.id}
              activeBiweekly={activeBiweekly}
              cadence={
                hasAuthoritativeSessionsData ? cadence[coach.id] : undefined
              }
              coach={coach}
              compactSecondaryActions={
                presentation === 'default' && visibleCoaches.length > ONE
              }
              hasPreviousSessions={sessionState === 'has_previous'}
              memberCoachMatchState={memberCoachMatchState}
              now={now}
              presentation={presentation}
              refetch={refetch}
              sessionState={sessionState}
              user={user}
              onUpdateBiweekly={setRescheduleCoach}
            />
          );
        })}
      </div>
      {rescheduleCoach && (
        <MemberBiweeklyReschedule
          coach={rescheduleCoach}
          onClose={() => setRescheduleCoach(null)}
          onDone={() => {
            setRescheduleCoach(null);
            refetch();
          }}
        />
      )}
    </>
  );
};

export default ClientCoaches;
